import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { submitContact } from '../../api/contact'
import Button from '../ui/Button'

const PACKAGES = [
  'Small Celebration',
  'Medium Celebration',
  'Premium Experience',
  'Not sure yet',
]

// ─── Field wrapper ─────────────────────────────────────────────────────────────
function Field({ label, error, children }) {
  return (
    <div>
      <label className="block text-sm font-body font-semibold text-ink mb-2">
        {label}
      </label>
      {children}
      <AnimatePresence>
        {error && (
          <motion.p
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="text-red-500 text-xs mt-1 font-body"
          >
            {error}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  )
}

// ─── Component ─────────────────────────────────────────────────────────────────
export default function ContactForm() {
  const location           = useLocation()
  const preselected        = location.state?.package || ''
  const [status, setStatus]= useState('idle')   // idle | loading | success | error
  const [serverErr, setServerErr] = useState('')

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: { package: preselected },
  })

  const onSubmit = async (data) => {
    setStatus('loading')
    setServerErr('')
    try {
      await submitContact(data)
      setStatus('success')
      reset()
    } catch (err) {
      setStatus('error')
      setServerErr(err.message || 'Something went wrong. Please try again.')
    }
  }

  // ── Success state ─────────────────────────────────────────────────────────
  if (status === 'success') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-surface rounded-3xl p-10 shadow-card text-center h-full flex flex-col items-center justify-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 300, delay: 0.2 }}
          className="text-7xl mb-6"
        >
          🎉
        </motion.div>
        <h3 className="font-display text-ink text-2xl mb-3">Request Sent!</h3>
        <p className="font-body text-ink-muted mb-2">
          Thank you! We'll contact you within 24 hours. 🎈
        </p>
        <p className="font-body text-ink-light text-sm mb-8">
          Check your WhatsApp and phone for our confirmation.
        </p>
        <Button onClick={() => setStatus('idle')} variant="secondary">
          Send Another Message
        </Button>
      </motion.div>
    )
  }

  // ── Form ──────────────────────────────────────────────────────────────────
  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="bg-surface rounded-3xl p-8 md:p-10 shadow-card space-y-6"
      noValidate
    >
      {/* Header */}
      <div>
        <h2 className="font-display text-primary text-2xl mb-1">Send Us a Message</h2>
        <p className="font-body text-ink-muted text-sm">
          Fill in the details and we'll get back to you within hours.
        </p>
      </div>

      {/* Name + Phone */}
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Your Name *" error={errors.name?.message}>
          <input
            {...register('name', {
              required:  'Name is required',
              minLength: { value: 2, message: 'At least 2 characters' },
            })}
            placeholder="e.g. Sara Hassan"
            className={['input-base', errors.name ? 'input-error' : ''].join(' ')}
          />
        </Field>

        <Field label="Phone Number *" error={errors.phone?.message}>
          <input
            {...register('phone', {
              required: 'Phone is required',
              pattern:  { value: /^[\d\s+\-()]{7,20}$/, message: 'Enter a valid phone number' },
            })}
            placeholder="e.g. 01012345678"
            type="tel"
            className={['input-base', errors.phone ? 'input-error' : ''].join(' ')}
          />
        </Field>
      </div>

      {/* Date + Package */}
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Event Date *" error={errors.event_date?.message}>
          <input
            {...register('event_date', { required: 'Event date is required' })}
            type="date"
            min={new Date().toISOString().split('T')[0]}
            className={['input-base', errors.event_date ? 'input-error' : ''].join(' ')}
          />
        </Field>

        <Field label="Select Package" error={errors.package?.message}>
          <select
            {...register('package')}
            className="input-base"
          >
            <option value="">-- Choose a package --</option>
            {PACKAGES.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </Field>
      </div>

      {/* Details */}
      <Field label="Additional Details" error={errors.message?.message}>
        <textarea
          {...register('message', {
            maxLength: { value: 2000, message: 'Max 2000 characters' },
          })}
          placeholder="Tell us about your child's dream theme, number of guests, or any special requests..."
          rows={4}
          className={['input-base resize-none', errors.message ? 'input-error' : ''].join(' ')}
        />
      </Field>

      {/* Server error */}
      <AnimatePresence>
        {serverErr && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-2xl px-4 py-3"
          >
            <span className="text-red-500 text-lg shrink-0">⚠️</span>
            <p className="font-body text-sm text-red-600">{serverErr}</p>
          </motion.div>
        )}
      </AnimatePresence>

      <Button
        type="submit"
        loading={status === 'loading'}
        disabled={status === 'loading'}
        className="w-full"
        size="lg"
      >
        🎉 Send My Request
      </Button>

      <p className="text-center text-xs font-body text-ink-light">
        We typically respond within 2–4 hours during working hours.
      </p>
    </form>
  )
}
